import React from 'react';
import { Box } from '@mui/material';

interface AnimatedBackgroundProps {
  children: React.ReactNode;
}

const AnimatedBackground: React.FC<AnimatedBackgroundProps> = ({
  children
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100vw',
        position: 'fixed',
        top: 0,
        left: 0,
        overflow: 'hidden',
        margin: 0,
        padding: 0,
        background:
          'linear-gradient(135deg, #f8fcff 0%, #f0f8ff 50%, #e6f3ff 100%)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(103, 183, 230, 0.06) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(113, 201, 253, 0.05) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(49, 177, 253, 0.04) 0%, transparent 50%)
          `,
          animation: 'gradientShift 8s ease-in-out infinite'
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
              linear-gradient(rgba(103, 183, 230, 0.12) 1px, transparent 1px),
              linear-gradient(90deg, rgba(103, 183, 230, 0.12) 1px, transparent 1px)
            `,
          backgroundSize: '40px 40px',
          animation: 'gridMove 20s linear infinite'
        },
        '@keyframes gradientShift': {
          '0%, 100%': {
            transform: 'translateX(0) translateY(0)'
          },
          '25%': {
            transform: 'translateX(-10px) translateY(-5px)'
          },
          '50%': {
            transform: 'translateX(5px) translateY(-10px)'
          },
          '75%': {
            transform: 'translateX(-5px) translateY(5px)'
          }
        },
        '@keyframes gridMove': {
          '0%': {
            transform: 'translate(0, 0)'
          },
          '100%': {
            transform: 'translate(40px, 40px)'
          }
        }
      }}
    >
      {/* Floating geometric shapes */}
      <Box
        className="floating-shapes"
        sx={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: { xs: '40px', sm: '50px', md: '60px' },
          height: { xs: '40px', sm: '50px', md: '60px' },
          background: 'rgba(103, 183, 230, 0.04)',
          borderRadius: '50%',
          animation: 'float 6s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': {
              transform: 'translateY(0px) rotate(0deg)'
            },
            '50%': {
              transform: 'translateY(-20px) rotate(180deg)'
            }
          },
          '@media (max-width: 600px)': {
            display: 'none'
          }
        }}
      />
      <Box
        className="floating-shapes"
        sx={{
          position: 'absolute',
          top: '20%',
          right: '15%',
          width: { xs: '30px', sm: '35px', md: '40px' },
          height: { xs: '30px', sm: '35px', md: '40px' },
          background: 'rgba(113, 201, 253, 0.03)',
          borderRadius: '8px',
          animation: 'float 8s ease-in-out infinite reverse',
          '@keyframes float': {
            '0%, 100%': {
              transform: 'translateY(0px) rotate(0deg)'
            },
            '50%': {
              transform: 'translateY(-15px) rotate(180deg)'
            }
          },
          '@media (max-width: 600px)': {
            display: 'none'
          }
        }}
      />
      <Box
        className="floating-shapes"
        sx={{
          position: 'absolute',
          bottom: '15%',
          left: '20%',
          width: { xs: '50px', sm: '65px', md: '80px' },
          height: { xs: '50px', sm: '65px', md: '80px' },
          background: 'rgba(49, 177, 253, 0.02)',
          borderRadius: '50%',
          animation: 'float 10s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': {
              transform: 'translateY(0px) rotate(0deg)'
            },
            '50%': {
              transform: 'translateY(-25px) rotate(180deg)'
            }
          },
          '@media (max-width: 600px)': {
            display: 'none'
          }
        }}
      />
      <Box
        className="floating-shapes"
        sx={{
          position: 'absolute',
          bottom: '25%',
          right: '10%',
          width: { xs: '35px', sm: '42px', md: '50px' },
          height: { xs: '35px', sm: '42px', md: '50px' },
          background: 'rgba(103, 183, 230, 0.03)',
          borderRadius: '12px',
          animation: 'float 7s ease-in-out infinite reverse',
          '@keyframes float': {
            '0%, 100%': {
              transform: 'translateY(0px) rotate(0deg)'
            },
            '50%': {
              transform: 'translateY(-18px) rotate(180deg)'
            }
          },
          '@media (max-width: 600px)': {
            display: 'none'
          }
        }}
      />

      {/* Animated dots pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: '30%',
          left: '5%',
          width: { xs: '3px', sm: '4px' },
          height: { xs: '3px', sm: '4px' },
          background: 'rgba(103, 183, 230, 0.15)',
          borderRadius: '50%',
          animation: 'pulse 2s ease-in-out infinite',
          '@keyframes pulse': {
            '0%, 100%': {
              transform: 'scale(1)',
              opacity: 0.4
            },
            '50%': {
              transform: 'scale(1.5)',
              opacity: 0.8
            }
          },
          '@media (max-width: 600px)': {
            display: 'none'
          }
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '60%',
          right: '8%',
          width: { xs: '4px', sm: '5px', md: '6px' },
          height: { xs: '4px', sm: '5px', md: '6px' },
          background: 'rgba(113, 201, 253, 0.12)',
          borderRadius: '50%',
          animation: 'pulse 3s ease-in-out infinite',
          '@keyframes pulse': {
            '0%, 100%': {
              transform: 'scale(1)',
              opacity: 0.3
            },
            '50%': {
              transform: 'scale(1.8)',
              opacity: 0.7
            }
          },
          '@media (max-width: 600px)': {
            display: 'none'
          }
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '40%',
          left: '8%',
          width: { xs: '2px', sm: '3px' },
          height: { xs: '2px', sm: '3px' },
          background: 'rgba(49, 177, 253, 0.2)',
          borderRadius: '50%',
          animation: 'pulse 2.5s ease-in-out infinite',
          '@keyframes pulse': {
            '0%, 100%': {
              transform: 'scale(1)',
              opacity: 0.5
            },
            '50%': {
              transform: 'scale(1.6)',
              opacity: 0.9
            }
          },
          '@media (max-width: 600px)': {
            display: 'none'
          }
        }}
      />

      {/* Main content with transparent white background */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 10,
          background: 'rgba(255, 255, 255, 0.3)',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          padding: { xs: '20px', sm: '30px', md: '40px' },
          boxShadow: '0 8px 32px rgba(103, 183, 230, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          animation: 'slideIn 0.8s ease-out',
          maxWidth: { xs: '90%', sm: '400px', md: '450px' },
          width: '100%',
          '@keyframes slideIn': {
            '0%': {
              transform: 'translateY(30px)',
              opacity: 0
            },
            '100%': {
              transform: 'translateY(0)',
              opacity: 1
            }
          },
          // Responsive adjustments for mobile
          '@media (max-width: 600px)': {
            margin: '20px',
            padding: '20px',
            borderRadius: '16px',
            '& .floating-shapes': {
              display: 'none' // Hide floating shapes on mobile for better performance
            }
          }
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default AnimatedBackground;
